/**
 * API Service — Zentraler HTTP-Client
 *
 * httpOnly Cookies (E13): withCredentials:true, kein Token im Header.
 * 401 Interceptor: auto refresh → retry, logout bei Refresh-Fail.
 */

import axios from 'axios';
import type { AuthUser } from '../stores/authStore';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true,
});

// ── 401 Interceptor (Auto-Refresh) ──────────────────────────────────────────

let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: any) => void; reject: (e: any) => void }> = [];

function processQueue(error: any) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(undefined)));
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Nur bei 401 auf geschützten Endpoints Token-Refresh versuchen
    // Auth-Endpoints (/auth/*) und bereits retried Requests überspringen
    const isAuthEndpoint = originalRequest.url?.startsWith('/auth/');
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      isAuthEndpoint
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => apiClient(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await apiClient.post('/auth/refresh');
      processQueue(null);
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      // Refresh ist endgueltig fehlgeschlagen — User-Session ist tot.
      // Statt nur den Original-Request zu rejecten (was das aufrufende Component
      // mit "Ungueltiges Refresh-Token"-Banner zeigt und den User auf der Seite
      // haengen laesst), forcieren wir hier den Logout: lokalen Auth-State
      // leeren + Hard-Redirect zur Login-Seite. Hard-Redirect statt Router.navigate
      // weil wir aus einem Axios-Interceptor heraus laufen — kein React-Context.
      try {
        // Lokaler Store leeren (auch wenn Session-Cookie schon weg ist)
        const { useAuthStore } = await import('../stores/authStore');
        useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false });
      } catch { /* import-Fehler ignorieren */ }
      // Nicht aus den Auth-Routes selbst zur Login-Seite werfen
      // (z.B. wenn /auth/refresh selbst der Original-Call war).
      const onLoginPage = window.location.pathname.startsWith('/login');
      if (!onLoginPage) {
        // H9 Defense-in-Depth: nur relative Pfade als next-Parameter erlauben.
        // Aktuell baut next aus location.pathname + .search — beide sind immer
        // relativ. Whitelist verhindert dass sich das aendert wenn jemand mal
        // window.location.href oder einen externen Wert hier reinpackt.
        const candidate = window.location.pathname + window.location.search;
        const safe = isSafeRedirectPath(candidate) ? candidate : '/';
        const next = encodeURIComponent(safe);
        window.location.replace(`/login?next=${next}&reason=session_expired`);
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ── Safe-Redirect (H9, 2026-05-05) ──────────────────────────────────────────

/**
 * Pruefr ob ein Pfad als Login-Redirect-Ziel sicher ist.
 *
 * Zulaessig:
 *   - "/" oder "/programme/x" oder "/settings?tab=2" (relative App-Pfade)
 * Nicht zulaessig (Open-Redirect-Vector):
 *   - "https://evil.com" oder "//evil.com" (Schema- oder Protocol-Relative-URL)
 *   - "/\evil.com" oder Backslash-Tricks
 *   - Pfade mit "@" (User-Info-URL)
 *   - Leerstrings oder undefined
 */
export function isSafeRedirectPath(path: string | null | undefined): boolean {
  if (!path) return false;
  // Muss mit / starten
  if (path[0] !== '/') return false;
  // "/" allein ist OK (Home). Alles laenger als 1 Zeichen darf nicht "//" oder "/\\" haben
  if (path.length > 1 && (path[1] === '/' || path[1] === '\\')) return false;
  // Keine User-Info-URLs zulassen
  if (path.includes('@')) return false;
  // Schema-Praefixe explizit blocken (z.B. "javascript:")
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path)) return false;
  return true;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProgrammeOwnerInfo {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  assignedAt: string;
}

export interface ProgrammeInfo {
  id: string;
  name: string;
  description: string;
  version: string;
  icon: string;
  category: string;
  /** Sichtbare Programm-Nummer (Anzeige "<nr>_<name>"). */
  programmNr: number;
  /** Kategorie-Nummer aus der zentralen Registry (core/backend/src/config/categories.ts). */
  kategorieNr: number;
  /** Kategorie-Icon aus der zentralen Registry. */
  categoryIcon: string;
  executionDisplay?: import('../components/ExecutionsTab').ExecutionDisplay;
  monitoring?: {
    expectedInterval?: number; // Minuten
    alertAfter?: number;       // Minuten
  };
  lastRun?: {
    status: 'pending' | 'running' | 'completed' | 'failed';
    completedAt: string | null;
  };
  owner: ProgrammeOwnerInfo | null;
}

export interface JobStatus {
  id: string;
  programmeId: string;
  jobType: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progressCurrent: number;
  progressTotal: number;
  progressMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  resultSummary: Record<string, any> | null;
  error?: string;
  createdAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: { code: string; message: string };
}

export interface ProgrammantragMeta {
  kategorien: Array<{ nr: number; key: string; name: string }>;
  programme: Array<{ id: string; name: string; category: string; programmNr?: number }>;
  schnittstellen: Array<{ key: string; label: string }>;
  prioritaeten: string[];
  stati: Array<{ key: string; label: string }>;
  freigeber: Array<{ id: number; name: string; email: string }>;
  users: Array<{ id: number; name: string; email: string }>;
}

// ── API Methods ──────────────────────────────────────────────────────────────

export const api = {
  // Auth
  auth: {
    async login(email: string, password: string): Promise<AuthUser> {
      const { data } = await apiClient.post<ApiResponse<{ user: AuthUser }>>('/auth/login', { email, password });
      return data.data.user;
    },
    async logout(): Promise<void> {
      await apiClient.post('/auth/logout');
    },
    async refresh(): Promise<void> {
      await apiClient.post('/auth/refresh');
    },
    async me(): Promise<AuthUser> {
      const { data } = await apiClient.get<ApiResponse<{ user: any; permissions: string[] }>>('/auth/me');
      return { ...data.data.user, permissions: data.data.permissions };
    },
    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
      await apiClient.post('/auth/change-password', { currentPassword, newPassword });
    },
    async forgotPassword(email: string): Promise<void> {
      await apiClient.post('/auth/forgot-password', { email });
    },
    async resetPassword(token: string, newPassword: string): Promise<void> {
      await apiClient.post('/auth/reset-password', { token, newPassword });
    },
  },

  // Users
  users: {
    async list(): Promise<any[]> {
      const { data } = await apiClient.get<ApiResponse<any[]>>('/users');
      return data.data ?? [];
    },
    async get(id: number): Promise<any> {
      const { data } = await apiClient.get<ApiResponse<any>>(`/users/${id}`);
      return data.data;
    },
    async update(id: number, updates: Record<string, any>): Promise<any> {
      const { data } = await apiClient.put<ApiResponse<any>>(`/users/${id}`, updates);
      return data.data;
    },
    async delete(id: number): Promise<void> {
      await apiClient.delete(`/users/${id}`);
    },
    async updatePermissions(
      id: number,
      permissions: string[],
    ): Promise<{ warnings?: Array<{ code: string; message: string; details?: any }> }> {
      const { data } = await apiClient.put<{ success: boolean; warnings?: Array<{ code: string; message: string; details?: any }> }>(
        `/users/${id}/permissions`,
        { permissions },
      );
      return { warnings: data.warnings };
    },
    async listRoles(): Promise<Array<{ id: number; name: string; displayName: string; hierarchyLevel: number; description?: string }>> {
      const { data } = await apiClient.get<ApiResponse<any[]>>('/users/roles');
      return data.data ?? [];
    },
  },

  // Rollen-Templates (Phase 3)
  roles: {
    async getDefaults(roleId: number): Promise<{ role: { id: number; name: string; displayName: string; hierarchyLevel: number }; permissions: string[] }> {
      const { data } = await apiClient.get<ApiResponse<any>>(`/roles/${roleId}/permissions`);
      return data.data;
    },
    async setDefaults(roleId: number, permissions: string[]): Promise<void> {
      await apiClient.put(`/roles/${roleId}/permissions`, { permissions });
    },
    async applyToUser(roleId: number, userId: number): Promise<{ userId: number; roleName: string; permissions: string[]; count: number }> {
      const { data } = await apiClient.post<ApiResponse<any>>(`/roles/${roleId}/apply-to-user/${userId}`);
      return data.data;
    },
  },

  // Invitations
  invitations: {
    async create(email: string, roleId: number, permissions?: string[]): Promise<any> {
      const { data } = await apiClient.post<ApiResponse<any>>('/invitations', { email, roleId, permissions });
      return data.data;
    },
    async accept(token: string, firstName: string, lastName: string, password: string): Promise<void> {
      await apiClient.post('/invitations/accept', { token, firstName, lastName, password });
    },
    async verify(token: string): Promise<any> {
      const { data } = await apiClient.get<ApiResponse<any>>(`/invitations/verify/${token}`);
      return data.data;
    },
    async list(): Promise<any[]> {
      const { data } = await apiClient.get<ApiResponse<any[]>>('/invitations');
      return data.data ?? [];
    },
    async revoke(id: number): Promise<void> {
      await apiClient.delete(`/invitations/${id}`);
    },
  },

  // Programmes
  async getProgrammes(): Promise<ProgrammeInfo[]> {
    const { data } = await apiClient.get<ApiResponse<ProgrammeInfo[]>>('/programmes');
    return data.data;
  },

  // Programm-Owner
  programmeOwners: {
    async claim(programmeId: string): Promise<{ programmeId: string; owner: ProgrammeOwnerInfo | null }> {
      const { data } = await apiClient.post<ApiResponse<{ programmeId: string; owner: ProgrammeOwnerInfo | null }>>(
        `/programmes/${programmeId}/claim`,
      );
      return data.data;
    },
    async release(programmeId: string): Promise<{ programmeId: string; owner: ProgrammeOwnerInfo | null }> {
      const { data } = await apiClient.delete<ApiResponse<{ programmeId: string; owner: ProgrammeOwnerInfo | null }>>(
        `/programmes/${programmeId}/owner`,
      );
      return data.data;
    },
    async setOwner(
      programmeId: string,
      userId: number | null,
    ): Promise<{ programmeId: string; owner: ProgrammeOwnerInfo | null }> {
      const { data } = await apiClient.put<ApiResponse<{ programmeId: string; owner: ProgrammeOwnerInfo | null }>>(
        `/programmes/${programmeId}/owner`,
        { userId },
      );
      return data.data;
    },
  },

  // Programmanträge (Antragswesen)
  programmantraege: {
    async list(status?: string): Promise<any[]> {
      const { data } = await apiClient.get<ApiResponse<any[]>>('/programmantraege', {
        params: status ? { status } : undefined,
      });
      return data.data ?? [];
    },
    async meta(): Promise<ProgrammantragMeta> {
      const { data } = await apiClient.get<ApiResponse<ProgrammantragMeta>>('/programmantraege/meta');
      return data.data;
    },
    async get(id: number): Promise<any> {
      const { data } = await apiClient.get<ApiResponse<any>>(`/programmantraege/${id}`);
      return data.data;
    },
    async create(fields: Record<string, any>): Promise<any> {
      const { data } = await apiClient.post<ApiResponse<any>>('/programmantraege', fields);
      return data.data;
    },
    async patch(id: number, fields: Record<string, any>): Promise<any> {
      const { data } = await apiClient.patch<ApiResponse<any>>(`/programmantraege/${id}`, fields);
      return data.data;
    },
    async submit(id: number): Promise<{ ok: boolean; data?: any; missing?: Array<{ field: string; label: string }> }> {
      try {
        const { data } = await apiClient.post<ApiResponse<any>>(`/programmantraege/${id}/submit`);
        return { ok: true, data: data.data };
      } catch (e: any) {
        const err = e?.response?.data?.error;
        if (e?.response?.status === 422 && err?.missing) {
          return { ok: false, missing: err.missing };
        }
        throw e;
      }
    },
    async addKommentar(id: number, text: string): Promise<any> {
      const { data } = await apiClient.post<ApiResponse<any>>(`/programmantraege/${id}/kommentar`, { text });
      return data.data;
    },
    async setStatus(
      id: number,
      status: string,
      extra?: { ablehnungsgrund?: string; umgesetztProgrammeId?: string },
    ): Promise<any> {
      const { data } = await apiClient.post<ApiResponse<any>>(`/programmantraege/${id}/status`, { status, ...extra });
      return data.data;
    },
    async getPrompt(id: number): Promise<string> {
      const { data } = await apiClient.get<ApiResponse<{ prompt: string }>>(`/programmantraege/${id}/prompt`);
      return data.data.prompt;
    },
    async remove(id: number): Promise<void> {
      await apiClient.delete(`/programmantraege/${id}`);
    },
    async badge(): Promise<number> {
      const { data } = await apiClient.get<ApiResponse<{ count: number }>>('/programmantraege/badge');
      return data.data?.count ?? 0;
    },
    async uploadAnhang(id: number, file: File, onProgress?: (pct: number) => void): Promise<any[]> {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await apiClient.post<ApiResponse<{ anhaenge: any[] }>>(`/programmantraege/${id}/anhang`, fd, {
        onUploadProgress: (ev) => {
          if (ev.total) onProgress?.(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      return data.data.anhaenge ?? [];
    },
    async deleteAnhang(id: number, url: string): Promise<any[]> {
      const { data } = await apiClient.delete<ApiResponse<{ anhaenge: any[] }>>(`/programmantraege/${id}/anhang`, { data: { url } });
      return data.data.anhaenge ?? [];
    },
  },

  // Jobs
  async getJobStatus(jobId: string): Promise<JobStatus> {
    const { data } = await apiClient.get<ApiResponse<JobStatus>>(`/jobs/${jobId}`);
    return data.data;
  },

  async executeProgramme(programmeId: string, params?: Record<string, any>): Promise<{ jobId: string }> {
    const { data } = await apiClient.post<ApiResponse<{ jobId: string }>>(
      `/programmes/${programmeId}/execute`,
      params
    );
    return data.data;
  },

  // History
  history: {
    async getStats(): Promise<any> {
      const { data } = await apiClient.get<ApiResponse<any>>('/history/stats');
      return data.data;
    },
    async getJobs(params?: { jobType?: string; status?: string; programmeId?: string; limit?: number; offset?: number }): Promise<{ jobs: any[]; total: number }> {
      const { data } = await apiClient.get<ApiResponse<{ jobs: any[]; total: number }>>('/history/jobs', { params });
      return data.data;
    },
    async getDashboardLastRuns(): Promise<any[]> {
      const { data } = await apiClient.get<ApiResponse<any[]>>('/history/dashboard-last-runs');
      return data.data;
    },
  },

  // Flows
  flows: {
    getCatalog() {
      return apiClient.get('/flows/catalog').then(r => r.data);
    },
    getCatalogItem(flowId: string) {
      return apiClient.get(`/flows/catalog/${flowId}`).then(r => r.data);
    },
    executeById(flowId: string, params: Record<string, any> = {}, debugMode: boolean = false) {
      return apiClient.post(`/flows/catalog/${flowId}/execute`, { params, debugMode }).then(r => r.data);
    },
    getExecutions(filters?: { flowId?: string; status?: string; limit?: number; offset?: number }) {
      return apiClient.get('/flows/v2/executions', { params: filters }).then(r => r.data);
    },
    getExecutionById(id: string) {
      return apiClient.get(`/flows/v2/executions/${id}`).then(r => r.data);
    },
    getStepData(executionId: string, stepId: string) {
      return apiClient.get(`/flows/v2/executions/${executionId}/steps/${stepId}/data`).then(r => r.data);
    },
    cancelExecution(id: string) {
      return apiClient.post(`/flows/v2/executions/${id}/cancel`).then(r => r.data);
    },
    continueExecution(id: string) {
      return apiClient.post(`/flows/v2/executions/${id}/continue`).then(r => r.data);
    },
    deleteExecution(executionId: string) {
      return apiClient.delete(`/flows/v2/executions/${executionId}`).then(r => r.data);
    },
    getSchedules() {
      return apiClient.get('/flows/schedules').then(r => r.data);
    },
    updateSchedule(flowId: string, data: { intervalMinutes: number; enabled?: boolean; notifyOnError?: string[]; pauseHours?: number[] | null; pauseDays?: number[] | null; params?: Record<string, any>; scheduleMode?: 'interval' | 'fixedTimes'; customTimes?: string[] | null }) {
      return apiClient.put(`/flows/schedules/${flowId}`, data).then(r => r.data);
    },
    deleteSchedule(flowId: string) {
      return apiClient.delete(`/flows/schedules/${flowId}`).then(r => r.data);
    },
    getDatastoreByFlowId(flowId: string) {
      return apiClient.get(`/flows/flow-datastores/${flowId}`).then(r => r.data);
    },
    getDatastoreByName(name: string) {
      return apiClient.get(`/flows/datastores/by-name/${encodeURIComponent(name)}`).then(r => r.data);
    },
    getDatastoreEntries(id: number, params?: { page?: number; pageSize?: number }) {
      return apiClient.get(`/flows/datastores/${id}/entries`, { params }).then(r => r.data);
    },
    getDatastoreStats(id: number) {
      return apiClient.get(`/flows/datastores/${id}/stats`).then(r => r.data);
    },
    getStatistics(flowId: string, days: number = 30) {
      return apiClient.get(`/flows/statistics/${flowId}`, { params: { days } }).then(r => r.data);
    },
    getRunDetails(flowId: string, executionId: string, page: number = 1, pageSize: number = 50) {
      return apiClient.get(`/flows/statistics/${flowId}/runs/${executionId}/details`, { params: { page, pageSize } }).then(r => r.data);
    },
    getJobHistory(params?: { jobType?: string; status?: string; limit?: number; offset?: number }) {
      return apiClient.get('/history/jobs', { params }).then(r => r.data);
    },
    // Connections
    getConnections() {
      return apiClient.get('/flows/connections').then(r => r.data);
    },
    getConnection(id: number) {
      return apiClient.get(`/flows/connections/${id}`).then(r => r.data);
    },
    createConnection(data: { name: string; type: string; description?: string; config: Record<string, any> }) {
      return apiClient.post('/flows/connections', data).then(r => r.data);
    },
    updateConnection(id: number, data: Partial<{ name: string; type: string; description: string; config: Record<string, any>; isActive: boolean }>) {
      return apiClient.put(`/flows/connections/${id}`, data).then(r => r.data);
    },
    deleteConnection(id: number) {
      return apiClient.delete(`/flows/connections/${id}`).then(r => r.data);
    },
    testConnection(id: number) {
      return apiClient.post(`/flows/connections/${id}/test`).then(r => r.data);
    },
  },

  // Raw HTTP methods — Programme-Frontends use these directly for custom endpoints
  get: <T = any>(url: string, config?: Parameters<typeof apiClient.get>[1]) =>
    apiClient.get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: Parameters<typeof apiClient.post>[2]) =>
    apiClient.post<T>(url, data, config),
  put: <T = any>(url: string, data?: any, config?: Parameters<typeof apiClient.put>[2]) =>
    apiClient.put<T>(url, data, config),
  delete: <T = any>(url: string, config?: Parameters<typeof apiClient.delete>[1]) =>
    apiClient.delete<T>(url, config),
  patch: <T = any>(url: string, data?: any, config?: Parameters<typeof apiClient.patch>[2]) =>
    apiClient.patch<T>(url, data, config),
};
